import { domUtilsService } from "../../../core/services/dom-utils.service";
import { htmlService } from "../../../core/services/html.service";
import { NS, STYLES, REFERENCES, ICONS, SCROLL_TO_BOTTOM_THRESHOLD, SCROLL_TO_TOP_THRESHOLD, CLICK, SCROLL, DOT } from "../../constants";
import type { IChatOptions } from "../../models";
import type { IKendoJQuery } from "../../../core";

declare const kendo: any;
declare const $: any;

export interface IScrollManagerContext {
    element: IKendoJQuery;
    options: IChatOptions;
    list: IKendoJQuery;
    onScrollNearTop?: () => void;
    onScrollNearBottom?: () => void;
    onScrollToLatest?: () => void;
    isLatestRangeActive?: () => boolean;
    hasLatestMessageRendered?: () => boolean;
}

export class ScrollManager {
    private context: IScrollManagerContext;
    private scrollToBottomButton: IKendoJQuery | null = null;
    private separator: IKendoJQuery | null = null;
    private _scrollHandler: (() => void) | null = null;
    private _clickHandler: ((e: any) => void) | null = null;
    private _olderLoadDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private _newerLoadDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
    _isLoadingOlder = false;
    _allOlderLoaded = false;
    _isLoadingNewer = false;
    _allNewerLoaded = true;
    private _scrolledAwayFromTop = false;
    private _scrolledAwayFromBottom = false;
    private _lastObservedClientHeight = 0;
    private _resizeObserverService: { destroy(): void } | null = null;

    constructor(context: IScrollManagerContext) {
        this.context = context;
        this._initScrollToBottomButton();
        this._initSeparator();
        this._attachEvents();
    }

    private _initScrollToBottomButton(): void {
        const options = this.context.options;

        if (options.scrollToBottomButton === false) {
            this.scrollToBottomButton = null;
            return;
        }

        const buttonHtml = kendo.html.renderButton(
            `<button class="${STYLES.scrollFab} ${STYLES.hidden}" ${REFERENCES.scrollToBottomButton} aria-label="Scroll to bottom"></button>`,
            {
                icon: ICONS.arrowDown,
                rounded: "full",
                themeColor: "base"
            }
        );

        this.scrollToBottomButton = $(buttonHtml).appendTo(this.context.element);
        const cssStyles = {
            position: "sticky",
            bottom: "16px",
            transform: "translateX(-50%)",
            zIndex: 1
        }

        const isContextElementRtl = this.context.element.css("direction") === "rtl";
        cssStyles[isContextElementRtl ? "right" : "left"] = "50%";

        this.scrollToBottomButton.css(cssStyles);
    }

    private _attachEvents(): void {
        this._scrollHandler = this._onScroll.bind(this);
        this._clickHandler = this._scrollToBottomButtonClick.bind(this);

        this.context.element
            .on(SCROLL + NS + ".scrollManager", this._scrollHandler)
            .on(CLICK + NS + ".scrollManager", `[${REFERENCES.scrollToBottomButton}]`, this._clickHandler);

        const containerElement = this.context.element[0];
        if (containerElement) {
            this._resizeObserverService = domUtilsService.createResizeObserver({
                element: containerElement,
                onResize: () => this._onContainerResize(),
                debounceTime: 1
            });
        }
    }

    private _onContainerResize(): void {
        const el = this.context.element[0];

        if (!el) {
            return;
        }

        const previousClientHeight = this._lastObservedClientHeight;
        const newClientHeight = el.clientHeight;

        this._lastObservedClientHeight = newClientHeight;

        if (previousClientHeight === 0 || newClientHeight === previousClientHeight) {
            return;
        }

        const newDistanceFromBottom = el.scrollHeight - el.scrollTop - newClientHeight;
        const previousDistanceFromBottom = newDistanceFromBottom + (newClientHeight - previousClientHeight);

        if (previousDistanceFromBottom <= SCROLL_TO_BOTTOM_THRESHOLD) {
            this.scrollToBottom();
        }
    }

    private _initSeparator(): void {
        this.separator = $(`<div class="${STYLES.messageBoxSeparator} ${STYLES.hidden}"></div>`);
        this.context.element.after(this.separator);
    }

    updateSeparator(): void {
        if (!this.separator) {
            return;
        }

        const el = this.context.element[0];
        const shouldShow = el.scrollHeight > el.clientHeight;

        if (shouldShow) {
            this.separator.removeClass(STYLES.hidden);
        } else {
            this.separator.addClass(STYLES.hidden);
        }
    }

    private _updateScrollToBottomButtonVisibility(): void {
        if (!this.scrollToBottomButton) {
            return;
        }

        const scrollTop = this.context.element.scrollTop() || 0;
        const scrollHeight = this.context.element.prop("scrollHeight") || 0;
        const clientHeight = this.context.element.prop("clientHeight") || 0;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const hasLatestMessageRendered = this.context.hasLatestMessageRendered ? this.context.hasLatestMessageRendered() : true;

        if (!hasLatestMessageRendered || distanceFromBottom > SCROLL_TO_BOTTOM_THRESHOLD) {
            this.scrollToBottomButton.removeClass(STYLES.hidden);
        } else {
            this.scrollToBottomButton.addClass(STYLES.hidden);
        }
    }

    private _onScroll(): void {
        this._updateScrollToBottomButtonVisibility();
        this.updateSeparator();

        const el = this.context.element[0];
        const scrollTop = el.scrollTop;
        const distanceFromBottom = el.scrollHeight - scrollTop - el.clientHeight;

        if (!this._scrolledAwayFromTop && scrollTop > SCROLL_TO_TOP_THRESHOLD) {
            this._clearOlderLoadDebounce();
            this._scrolledAwayFromTop = true;
        }

        if (!this._scrolledAwayFromBottom && distanceFromBottom > SCROLL_TO_BOTTOM_THRESHOLD) {
            this._clearNewerLoadDebounce();
            this._scrolledAwayFromBottom = true;
        }

        if (scrollTop <= SCROLL_TO_TOP_THRESHOLD && this._scrolledAwayFromTop && this.context.onScrollNearTop) {
            this._scrolledAwayFromTop = false;
            if (!this._isLoadingOlder && !this._allOlderLoaded) {
                this._debounceOlderLoad();
            }
        }

        if (distanceFromBottom <= SCROLL_TO_BOTTOM_THRESHOLD && this._scrolledAwayFromBottom && this.context.onScrollNearBottom) {
            this._scrolledAwayFromBottom = false;
            if (!this._isLoadingNewer && !this._allNewerLoaded) {
                this._debounceNewerLoad();
            }
        }
    }

    private _getEndlessScrollDebounceDelay(): number {
        return Math.max(0, this.context.options.endlessScrollDebounceDelay || 0);
    }

    private _clearOlderLoadDebounce(): void {
        if (this._olderLoadDebounceTimeout) {
            clearTimeout(this._olderLoadDebounceTimeout);
            this._olderLoadDebounceTimeout = null;
        }
    }

    private _clearNewerLoadDebounce(): void {
        if (this._newerLoadDebounceTimeout) {
            clearTimeout(this._newerLoadDebounceTimeout);
            this._newerLoadDebounceTimeout = null;
        }
    }

    private _isNearTopEdge(): boolean {
        return (this.context.element[0]?.scrollTop || 0) <= SCROLL_TO_TOP_THRESHOLD;
    }

    private _isNearBottomEdge(): boolean {
        const element = this.context.element[0];

        if (!element) {
            return false;
        }

        return element.scrollHeight - element.scrollTop - element.clientHeight <= SCROLL_TO_BOTTOM_THRESHOLD;
    }

    private _debounceOlderLoad(): void {
        const delay = this._getEndlessScrollDebounceDelay();

        this._clearOlderLoadDebounce();

        if (!delay) {
            this.context.onScrollNearTop?.();
            return;
        }

        this._olderLoadDebounceTimeout = setTimeout(() => {
            this._olderLoadDebounceTimeout = null;

            if (this._isNearTopEdge() && !this._isLoadingOlder && !this._allOlderLoaded) {
                this.context.onScrollNearTop?.();
            }
        }, delay);
    }

    private _debounceNewerLoad(): void {
        const delay = this._getEndlessScrollDebounceDelay();

        this._clearNewerLoadDebounce();

        if (!delay) {
            this.context.onScrollNearBottom?.();
            return;
        }

        this._newerLoadDebounceTimeout = setTimeout(() => {
            this._newerLoadDebounceTimeout = null;

            if (this._isNearBottomEdge() && !this._isLoadingNewer && !this._allNewerLoaded) {
                this.context.onScrollNearBottom?.();
            }
        }, delay);
    }

    private _scrollToBottomButtonClick(e: any): void {
        e.preventDefault();

        if (this.context.onScrollToLatest && this.context.isLatestRangeActive && !this.context.isLatestRangeActive()) {
            this.context.onScrollToLatest();
            return;
        }

        this.scrollToBottom();
    }

    private _getMessageElement(uid: string): IKendoJQuery {
        return this.context.list.find(DOT + STYLES.message + `[data-uid="${htmlService.encode(uid)}"]`) as IKendoJQuery;
    }

    private _getPreviousMessageElement(messageElement: IKendoJQuery): IKendoJQuery {
        return messageElement.prevAll(DOT + STYLES.message).first() as IKendoJQuery;
    }

    private _getPinnedHeight(): number {
        const pinnedElement = this.context.element.find(DOT + STYLES.messagePinned);

        return pinnedElement.length ? pinnedElement.outerHeight() || 0 : 0;
    }

    private _getAutoScrollThreshold(pinnedHeight: number): number {
        const availableHeight = Math.max((this.context.element.prop("clientHeight") || 0) - pinnedHeight, 0);
        const threshold = this.context.options.autoScrollThreshold;

        if (typeof threshold === "number") {
            return Math.max(threshold, 0);
        }

        if (typeof threshold === "string") {
            const trimmedThreshold = threshold.trim();

            if (trimmedThreshold.endsWith("%")) {
                const percentage = parseFloat(trimmedThreshold.slice(0, -1));

                if (!isNaN(percentage)) {
                    return Math.max((availableHeight * percentage) / 100, 0);
                }
            }

            const pixelThreshold = parseFloat(trimmedThreshold);

            if (!isNaN(pixelThreshold)) {
                return Math.max(pixelThreshold, 0);
            }
        }

        return 0;
    }

    preserveScrollPosition(callback: () => void): void {
        const container = this.context.element[0];
        const scrollTop = container.scrollTop;
        const previousScrollHeight = container.scrollHeight;
        callback();
        const newScrollHeight = container.scrollHeight;
        container.scrollTo({ top: scrollTop + (newScrollHeight - previousScrollHeight), behavior: "instant" });
    }

    scrollToBottom(): void {
        const container = this.context.element[0];
        container.scrollTo({ top: container.scrollHeight, behavior: "instant" });
    }

    isNearBottom(): boolean {
        const scrollTop = this.context.element.scrollTop() || 0;
        const scrollHeight = this.context.element.prop("scrollHeight") || 0;
        const clientHeight = this.context.element.prop("clientHeight") || 0;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        return distanceFromBottom <= SCROLL_TO_BOTTOM_THRESHOLD;
    }

    scrollToMessage(uid: string): boolean {
        if (!uid) {
            return false;
        }

        const messageElement = this._getMessageElement(uid);

        if (!messageElement.length) {
            return false;
        }

        const pinnedHeight = this._getPinnedHeight();

        return domUtilsService.scrollToElement(this.context.element, messageElement, { position: "top", offset: -pinnedHeight });
    }

    scrollIncomingMessage(uid: string): boolean {
        if (!uid) {
            return false;
        }

        const messageElement = this._getMessageElement(uid);

        if (!messageElement.length) {
            return false;
        }

        const container = this.context.element;
        const containerElement = container[0];
        const containerOffset = container.offset();
        const messageOffset = messageElement.offset();

        if (!containerElement || !containerOffset || !messageOffset) {
            return false;
        }

        const scrollTop = container.scrollTop() || 0;
        const containerHeight = containerElement.clientHeight || 0;
        const pinnedHeight = this._getPinnedHeight();
        const threshold = this._getAutoScrollThreshold(pinnedHeight);
        const messageTop = messageOffset.top - containerOffset.top + scrollTop;
        const preferredScrollTop = messageTop - pinnedHeight - threshold;
        const previousMessageElement = this._getPreviousMessageElement(messageElement);
        const maxScrollTop = containerElement.scrollHeight - containerHeight;
        let targetScrollTop = preferredScrollTop;

        if (previousMessageElement.length) {
            const previousMessageOffset = previousMessageElement.offset();

            if (previousMessageOffset) {
                const previousMessageBottom = previousMessageOffset.top - containerOffset.top + scrollTop + (previousMessageElement.outerHeight() || 0);
                const minimumPreviousContext = Math.min(threshold, 8);
                const visibleTop = preferredScrollTop + pinnedHeight;

                if (minimumPreviousContext > 0 && previousMessageBottom < visibleTop + minimumPreviousContext) {
                    targetScrollTop = previousMessageBottom - pinnedHeight - minimumPreviousContext;
                }
            }
        }

        targetScrollTop = Math.min(Math.max(targetScrollTop, 0), maxScrollTop);

        container.finish().animate({
            scrollTop: targetScrollTop
        }, 0, "linear");

        return true;
    }

    resetEndlessState(): void {
        this._clearOlderLoadDebounce();
        this._clearNewerLoadDebounce();
        this._isLoadingOlder = false;
        this._allOlderLoaded = false;
        this._isLoadingNewer = false;
        this._allNewerLoaded = true;
        this._scrolledAwayFromTop = false;
        this._scrolledAwayFromBottom = false;
    }

    refreshState(): void {
        this._updateScrollToBottomButtonVisibility();
        this.updateSeparator();
    }

    destroy(): void {
        this._clearOlderLoadDebounce();
        this._clearNewerLoadDebounce();

        if (this._resizeObserverService) {
            this._resizeObserverService.destroy();
            this._resizeObserverService = null;
        }

        if (this.scrollToBottomButton) {
            this.scrollToBottomButton.remove();
            this.scrollToBottomButton = null;
        }

        if (this.separator) {
            this.separator.remove();
            this.separator = null;
        }

        this.context.element.off(NS + ".scrollManager");
    }
}
